/**
 * The single source of truth for scroll-driven timing.
 *
 * Every visual state is a pure function of normalised progress `p` in [0, 1].
 * Nothing here reads the DOM, nothing holds state, and no value is duplicated in
 * SCSS or in the view controller — the controller turns these numbers into CSS
 * custom properties and the stylesheet only consumes them.
 *
 * `p = 1` spans the *scrollable* distance of the track (track height minus the
 * pinned stage), so one unit of p is SCROLL_TRACK_VH minus 100vh.
 */

export type SceneId = 'prompt' | 'desktop' | 'mobile' | 'signage' | 'email' | 'report';

/* -------------------------------------------------------------- pure helpers */

export const clamp = (value: number, min = 0, max = 1): number =>
  value < min ? min : value > max ? max : value;

/** Normalised position of `value` inside [from, to], clamped to [0, 1]. */
export const segment = (value: number, from: number, to: number): number =>
  from === to ? (value < from ? 0 : 1) : clamp((value - from) / (to - from));

export const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/* ------------------------------------------------------------- the phase map */

interface ScenePhase {
  /** Cross-fade in. `null` means the scene is already present at p = 0. */
  fadeIn: [number, number] | null;
  /** Cross-fade out. `null` means the scene never leaves (the final scene). */
  fadeOut: [number, number] | null;
  /**
   * A/B storyboard hand-off inside the hold, for the two surface scenes.
   * Drives the interface fade to 40% and the second row's dim.
   */
  ab?: [number, number];
}

/**
 * Retimed for Stage 2B.2.
 *
 * Stage 2B cut 1000vh of scrollable distance to 820 (Stage 2B.1), which read as
 * aggressive: scenes changed before a use case could be understood. This map
 * runs 1070vh — not a return to the Stage 2B numbers, but a re-weighting. The
 * extra progress goes into readable internal reveals, calmer entrances and
 * exits, and slightly longer stable states. Durations are weighted, not equal:
 * Report, Desktop, Email and Prompt carry the most, simple hand-offs the least.
 *
 * Stage 2C widened every scene-to-scene band from 0.022 to 0.045 about its own
 * centre, because at 0.022 one wheel notch (~100px) spanned the whole cross-fade
 * and the hand-over read as a cut. The width is taken symmetrically out of the
 * neighbouring holds, so the track length is exactly as before.
 *
 * Two centres then moved by 0.012 to buy Signage and Email a settle beat: they
 * assemble the most and were left standing still for under one wheel notch.
 * Mobile lends the first, Reports the second, and each scene keeps its own
 * internal run translated by the same amount so nothing arrives empty.
 */
export const PHASES: Record<SceneId, ScenePhase> = {
  prompt: {
    fadeIn: null,
    fadeOut: [0.1325, 0.1775],
  },
  desktop: {
    fadeIn: [0.1325, 0.1775],
    fadeOut: [0.327, 0.372],
    ab: [0.258, 0.301],
  },
  mobile: {
    fadeIn: [0.327, 0.372],
    fadeOut: [0.4815, 0.5265],
    ab: [0.437, 0.475],
  },
  signage: {
    fadeIn: [0.4815, 0.5265],
    fadeOut: [0.63, 0.675],
  },
  email: {
    fadeIn: [0.63, 0.675],
    fadeOut: [0.7895, 0.8345],
  },
  report: {
    fadeIn: [0.7895, 0.8345],
    fadeOut: null,
  },
};

/** Render order, and therefore stacking order, of the pinned scenes. */
export const SCENE_SEQUENCE: SceneId[] = [
  'prompt',
  'desktop',
  'mobile',
  'signage',
  'email',
  'report',
];

/**
 * Cross-fade shape. The two scenes in a band always sum to exactly 1, so no
 * frame is ever empty; easing the shared parameter means the band is spent
 * mostly near one end or the other rather than sitting at 50/50, which is what
 * made the Stage 2B transitions look ghosted.
 */
const crossFade = easeInOutCubic;

/**
 * Titles hand over inside the band without ever both being prominent: the
 * outgoing one is gone by 70% and the incoming one starts at 30%.
 *
 * The windows deliberately overlap. Sequencing them (out by 0.4, in from 0.6)
 * left the middle of every band with no heading at all, and because `crossFade`
 * is flat at both ends that silent band measured 92px even before Stage 2C
 * widened the bands. Overlapping them closes it to nothing while the eased ends
 * keep both headings under 0.25 throughout, so two titles never compete.
 */
const TITLE_OUT_BY = 0.7;
const TITLE_IN_FROM = 0.3;

/* ----------------------------------------------------------- derived helpers */

/** Scene opacity at `p`. Adjacent scenes sum to 1 at every point. */
export const sceneOpacity = (id: SceneId, p: number): number => {
  const { fadeIn, fadeOut } = PHASES[id];
  const rising = fadeIn ? crossFade(segment(p, fadeIn[0], fadeIn[1])) : 1;
  const falling = fadeOut ? 1 - crossFade(segment(p, fadeOut[0], fadeOut[1])) : 1;
  return Math.min(rising, falling);
};

/**
 * Scene travel in the range [-1, 1], scaled to px by the stylesheet. Eased so
 * the movement settles before the hold and only leaves once the hold is over.
 */
export const sceneTravel = (id: SceneId, p: number): number => {
  const { fadeIn, fadeOut } = PHASES[id];
  const entering = fadeIn ? 1 - easeOutCubic(segment(p, fadeIn[0], fadeIn[1])) : 0;
  const leaving = fadeOut ? easeOutCubic(segment(p, fadeOut[0], fadeOut[1])) : 0;
  return entering - leaving;
};

/**
 * Depth on the hand-off: the incoming scene resolves from very slightly small,
 * the outgoing one keeps going past the viewer. It shares `crossFade` with the
 * opacity, so the two are one gesture rather than two that need keeping in step.
 *
 * The two windows never overlap, so at most one factor is ever off 1 and the
 * product is exactly 1 through every hold — a scene at rest is untransformed.
 */
const SCALE_IN_FROM = 0.985;
const SCALE_OUT_TO = 1.01;

export const sceneScale = (id: SceneId, p: number): number => {
  const { fadeIn, fadeOut } = PHASES[id];
  const entering = fadeIn ? lerp(SCALE_IN_FROM, 1, crossFade(segment(p, fadeIn[0], fadeIn[1]))) : 1;
  const leaving = fadeOut
    ? lerp(1, SCALE_OUT_TO, crossFade(segment(p, fadeOut[0], fadeOut[1])))
    : 1;
  return entering * leaving;
};

/** Title opacity — sequenced inside the band so two headings never share it. */
export const titleOpacity = (id: SceneId, p: number): number => {
  const { fadeIn, fadeOut } = PHASES[id];
  const rising = fadeIn
    ? crossFade(segment(p, lerp(fadeIn[0], fadeIn[1], TITLE_IN_FROM), fadeIn[1]))
    : 1;
  const falling = fadeOut
    ? 1 - crossFade(segment(p, fadeOut[0], lerp(fadeOut[0], fadeOut[1], TITLE_OUT_BY)))
    : 1;
  return Math.min(rising, falling);
};

/**
 * Entry only. On exit the scene transform already carries the title upward, and
 * adding a second offset on top of it pushed the heading past the top of the
 * clipped stage — measured at -14px to -41px before this was corrected.
 */
export const titleTravel = (id: SceneId, p: number): number => {
  const { fadeIn } = PHASES[id];
  return fadeIn
    ? 1 - easeOutCubic(segment(p, lerp(fadeIn[0], fadeIn[1], TITLE_IN_FROM), fadeIn[1]))
    : 0;
};

/**
 * How present a scene's internal detail is: 1 while it is dominant, falling to
 * 0 over the first ~60% of its fade-out.
 *
 * The scene grounds cross-fade complementarily, so the frame is never empty;
 * this takes the outgoing scene's *content* out early, so a detailed incoming
 * composition is never competing with a detailed outgoing one. It is what stops
 * the transitions reading as two ghosted scenes laid over each other.
 */
const QUIET_BY = 0.62;

export const sceneQuiet = (id: SceneId, p: number): number => {
  const { fadeOut } = PHASES[id];
  return fadeOut
    ? 1 - crossFade(segment(p, fadeOut[0], lerp(fadeOut[0], fadeOut[1], QUIET_BY)))
    : 1;
};

/**
 * A/B hand-off for the surface scenes: 0 = keyframe A (interface at full
 * strength), 1 = keyframe B (interface at 40%, second row dimmed).
 */
export const abState = (id: SceneId, p: number): number => {
  const { ab } = PHASES[id];
  return ab ? easeInOutCubic(segment(p, ab[0], ab[1])) : 0;
};

/** The scene that currently owns the frame, for pointer and inert handling. */
export const dominantScene = (p: number): SceneId =>
  SCENE_SEQUENCE.reduce((best, id) => (sceneOpacity(id, p) > sceneOpacity(best, p) ? id : best));

/**
 * Background presets — one per visual section of the Figma storyboard.
 *
 * Node 1651:2588 contains nine sections: the hero, the Prompt panel, the two
 * Desktop keyframes, the two Mobile keyframes, Digital signage, Email and
 * Reports. Each preset is the background over that section's own viewport
 * window in the node, so it carries that section's colour order and
 * orientation rather than a slice of the repeating cycle. The frameY window
 * used for each is recorded beside its gradient in the stylesheet.
 */
export type BgPreset =
  | 'hero'
  | 'prompt'
  | 'desktopA'
  | 'desktopB'
  | 'mobileA'
  | 'mobileB'
  | 'signage'
  | 'email'
  | 'reportA'
  | 'reportB';

/**
 * When each preset gives way to the next.
 *
 * `blend` is the progress window over which this preset cross-fades into the
 * following one; outside those windows the background is completely static, so
 * scrolling inside a scene's hold does not change a single pixel. The windows
 * are centred on the scene transitions they belong to — the scene-to-scene
 * fades widened to twice the scene band so the colour change is gentler than
 * the scene change it accompanies, and the A/B hand-offs left at their own
 * width, which is already broad.
 */
export const BG_SCHEDULE: { preset: BgPreset; blend: [number, number] | null }[] = [
  { preset: 'hero', blend: null },
  { preset: 'prompt', blend: [0.14, 0.17] },
  { preset: 'desktopA', blend: [0.258, 0.301] },
  { preset: 'desktopB', blend: [0.334, 0.365] },
  { preset: 'mobileA', blend: [0.437, 0.475] },
  { preset: 'mobileB', blend: [0.501, 0.531] },
  { preset: 'signage', blend: [0.637, 0.668] },
  { preset: 'email', blend: [0.785, 0.815] },
  { preset: 'reportA', blend: [0.95, 1] },
  { preset: 'reportB', blend: null },
];

/** Where the hero preset gives way to the Prompt one, in opening-hand-off units. */
const HERO_BLEND: [number, number] = [0.35, 1];

/**
 * Which two presets the background is between, and how far.
 *
 * Returns `mix: 0` with both sides equal whenever a scene is established, so
 * the engine writes nothing and the rendered pixels are stable. Only inside a
 * blend window do the two sides differ, and then only one opacity changes —
 * the gradient images themselves are never interpolated or repositioned.
 */
export const backgroundState = (
  p: number,
  q: number,
): { from: BgPreset; to: BgPreset; mix: number } => {
  // The opening hand-off: the hero holds at the top of the page, then gives
  // way to the Prompt background as the stage comes up to the viewport.
  if (q < 1) {
    return {
      from: 'hero',
      to: 'prompt',
      mix: easeInOutCubic(segment(q, HERO_BLEND[0], HERO_BLEND[1])),
    };
  }

  for (let i = 1; i < BG_SCHEDULE.length; i += 1) {
    const { preset, blend } = BG_SCHEDULE[i];
    if (!blend) {
      break;
    }
    if (p < blend[0]) {
      return { from: preset, to: preset, mix: 0 };
    }
    if (p <= blend[1]) {
      return {
        from: preset,
        to: BG_SCHEDULE[i + 1].preset,
        mix: easeInOutCubic(segment(p, blend[0], blend[1])),
      };
    }
  }
  return { from: 'reportB', to: 'reportB', mix: 0 };
};

/**
 * The ten storyboard states, purely for documentation and deterministic QA —
 * nothing in the runtime branches on these.
 */
export const STORYBOARD = [
  { state: 'hero', at: 'before the track' },
  { state: 'prompt', at: 0.07 },
  { state: 'desktop-a', at: 0.24 },
  { state: 'desktop-b', at: 0.32 },
  { state: 'mobile-a', at: 0.43 },
  { state: 'mobile-b', at: 0.49 },
  { state: 'signage', at: 0.6 },
  { state: 'email', at: 0.77 },
  { state: 'report', at: 0.98 },
  { state: 'release', at: 'after the track' },
] as const;
